import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { functions } from "../lib/firebase";
import type { AddIcpSamplesRequest, IcpSampleContact } from "../lib/types";

interface ListResponse {
  samples: IcpSampleContact[];
}

interface MutationResponse {
  samples: IcpSampleContact[];
}

const callList = httpsCallable<{ icp_name: string }, ListResponse>(
  functions,
  "cf_list_icp_samples",
);
const callAdd = httpsCallable<AddIcpSamplesRequest, MutationResponse>(
  functions,
  "cf_add_icp_samples",
);
const callDelete = httpsCallable<
  { sample_id: number; icp_name: string },
  MutationResponse
>(functions, "cf_delete_icp_sample");
const callUpdate = httpsCallable<
  { sample_id: number; icp_name: string; notes: string | null },
  MutationResponse
>(functions, "cf_update_icp_sample");

const SAMPLES_KEY = (icpName: string) => ["icp-samples", icpName] as const;

export function useIcpSamples(icpName: string | null) {
  return useQuery<IcpSampleContact[], Error>({
    queryKey: SAMPLES_KEY(icpName ?? ""),
    queryFn: async () => {
      if (!icpName) return [];
      const res = await callList({ icp_name: icpName });
      return res.data.samples;
    },
    enabled: Boolean(icpName),
    staleTime: 30_000,
  });
}

export function useAddIcpSamples(icpName: string) {
  const qc = useQueryClient();
  return useMutation<IcpSampleContact[], Error, AddIcpSamplesRequest>({
    mutationFn: async (body) => {
      const res = await callAdd({ ...body, icp_name: icpName });
      return res.data.samples;
    },
    onSuccess: (samples) => {
      qc.setQueryData<IcpSampleContact[]>(SAMPLES_KEY(icpName), samples);
    },
  });
}

export function useAddIcpSamplesFor() {
  return useMutation<IcpSampleContact[], Error, AddIcpSamplesRequest>({
    mutationFn: async (body) => {
      const res = await callAdd(body);
      return res.data.samples;
    },
  });
}

export function useDeleteIcpSample(icpName: string) {
  const qc = useQueryClient();
  return useMutation<IcpSampleContact[], Error, { sample_id: number }>({
    mutationFn: async ({ sample_id }) => {
      const res = await callDelete({ sample_id, icp_name: icpName });
      return res.data.samples;
    },
    onSuccess: (samples) => {
      qc.setQueryData<IcpSampleContact[]>(SAMPLES_KEY(icpName), samples);
    },
  });
}

export function useDeleteIcpSamples(icpName: string) {
  const qc = useQueryClient();
  return useMutation<IcpSampleContact[], Error, { sample_ids: number[] }>({
    mutationFn: async ({ sample_ids }) => {
      let latest = qc.getQueryData<IcpSampleContact[]>(SAMPLES_KEY(icpName)) ?? [];
      for (const sample_id of sample_ids) {
        const res = await callDelete({ sample_id, icp_name: icpName });
        latest = res.data.samples;
      }
      return latest;
    },
    onSuccess: (samples) => {
      qc.setQueryData<IcpSampleContact[]>(SAMPLES_KEY(icpName), samples);
    },
  });
}

export function useUpdateIcpSample(icpName: string) {
  const qc = useQueryClient();
  return useMutation<
    IcpSampleContact[],
    Error,
    { sample_id: number; notes: string | null }
  >({
    mutationFn: async ({ sample_id, notes }) => {
      const res = await callUpdate({ sample_id, icp_name: icpName, notes });
      return res.data.samples;
    },
    onSuccess: (samples) => {
      qc.setQueryData<IcpSampleContact[]>(SAMPLES_KEY(icpName), samples);
    },
  });
}
